import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import PlatformLayout from '../../components/layout/PlatformLayout';
import './Platform.css';
import { Bug, AlertTriangle, HelpCircle, Lightbulb, Flag, ChevronDown, ChevronRight, Send } from 'lucide-react';

/**
 * Platform admin → Support — pilot triage queue for support_tickets
 * (migrate-v20). Open tickets float to the top; staff move them through
 * open → in progress → resolved/closed and can leave a resolution note,
 * which the member sees on their /report-problem page.
 *
 * 'club_request' tickets (B-5, migrate-v30) come from the no-club landing
 * state: vet the club → create it in Clubs → invite the requester as
 * founding admin → resolve the ticket. Form fields show as labelled rows.
 */

const CATEGORY_META = {
  bug: { label: 'Bug', icon: Bug, color: '#A33' },
  complaint: { label: 'Complaint', icon: AlertTriangle, color: '#C4943A' },
  question: { label: 'Question', icon: HelpCircle, color: '#3A5A8C' },
  idea: { label: 'Idea', icon: Lightbulb, color: '#1E4A35' },
  club_request: { label: 'Club request', icon: Flag, color: '#5A4A7C' },
};

// context keys worth surfacing as labelled rows on a club-request ticket
const CLUB_REQUEST_FIELDS = [
  ['clubName', 'Club'],
  ['sport', 'Sport'],
  ['role', 'Their role'],
  ['contact', 'Contact'],
];

const STATUSES = ['open', 'in_progress', 'resolved', 'closed'];
const STATUS_LABELS = { open: 'Open', in_progress: 'In progress', resolved: 'Resolved', closed: 'Closed' };
const STATUS_COLORS = { open: '#C4943A', in_progress: '#3A5A8C', resolved: '#1E4A35', closed: '#888' };

export default function PlatformSupport() {
  const { platformAxios } = usePlatformAuth();
  const qc = useQueryClient();
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [savedFlash, setSavedFlash] = useState(null); // ticket id that just saved

  const { data, isLoading } = useQuery({
    queryKey: ['platform-support-tickets', filter],
    queryFn: () => platformAxios.get(`/support-tickets${filter !== 'all' ? `?status=${filter}` : ''}`).then(r => r.data),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }) => platformAxios.patch(`/support-tickets/${id}`, patch).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-support-tickets'] }),
  });

  const sendReply = useMutation({
    mutationFn: ({ id, body }) => platformAxios.post(`/support-tickets/${id}/messages`, { body }).then(r => r.data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['platform-support-tickets'] });
      setNoteDrafts(d => ({ ...d, [id]: '' }));
      setSavedFlash(id);
      setTimeout(() => setSavedFlash(s => (s === id ? null : s)), 2500);
    },
  });

  // Status change with an unsent reply draft: send the reply first so typing
  // a message and hitting "→ Resolved" does both — no silent draft loss.
  const changeStatus = async (t, status) => {
    const draft = (noteDrafts[t.id] || '').trim();
    if (draft) await sendReply.mutateAsync({ id: t.id, body: draft }).catch(() => {});
    update.mutate({ id: t.id, patch: { status } });
  };

  const counts = data?.counts || {};
  const tickets = data?.tickets || [];
  const openCount = counts.open || 0;

  return (
    <PlatformLayout>
      <div className="platform-page">
        <div className="page-header">
          <div>
            <h1>Support</h1>
            <p>Pilot triage — bugs, complaints, questions, ideas and club requests{openCount ? ` · ${openCount} open` : ''}</p>
          </div>
          <div className="period-selector">
            {['all', ...STATUSES].map(s => (
              <button
                key={s}
                className={`period-btn ${filter === s ? 'active' : ''}`}
                onClick={() => setFilter(s)}
              >
                {s === 'all' ? `All` : `${STATUS_LABELS[s]}${counts[s] ? ` (${counts[s]})` : ''}`}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <p style={{ color: '#888' }}>Loading…</p>
        ) : tickets.length === 0 ? (
          <p style={{ color: '#888', padding: '24px 0' }}>
            {filter === 'all' ? 'No tickets yet — quiet out there. 👍' : `No ${STATUS_LABELS[filter]?.toLowerCase()} tickets.`}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {tickets.map(t => {
              const cat = CATEGORY_META[t.category] || CATEGORY_META.bug;
              const Icon = cat.icon;
              const open = expandedId === t.id;
              const draft = noteDrafts[t.id] ?? '';
              const busy = update.isPending || sendReply.isPending;
              return (
                <div key={t.id} style={{ background: '#fff', borderRadius: 10, border: '1px solid rgba(0,0,0,0.08)' }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer' }}
                    onClick={() => setExpandedId(open ? null : t.id)}
                  >
                    {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    <Icon size={16} style={{ color: cat.color, flexShrink: 0 }} title={cat.label} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#999', flexShrink: 0 }}>#{t.ticket_no}</span>
                    <span style={{
                      flex: 1, fontSize: 14, fontWeight: 600, color: '#2C2B28',
                      whiteSpace: open ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {t.message}
                    </span>
                    <span style={{ fontSize: 12.5, color: '#888', whiteSpace: 'nowrap' }}>
                      {t.user_name}{t.club_name ? ` · ${t.club_name}` : ''}
                    </span>
                    <span style={{
                      fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                      background: `${STATUS_COLORS[t.status]}22`, color: STATUS_COLORS[t.status], whiteSpace: 'nowrap',
                    }}>
                      {STATUS_LABELS[t.status]}
                    </span>
                  </div>

                  {open && (
                    <div style={{ padding: '0 14px 14px 39px', fontSize: 13.5, color: '#555' }}>
                      <div style={{ marginBottom: 8 }}>
                        <strong>{t.user_name}</strong> &lt;{t.user_email}&gt;
                        {t.club_name ? ` · ${t.club_name}` : ''} ·{' '}
                        {new Date(t.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        {t.context?.platform ? ` · ${t.context.platform}` : ''}
                        {t.context?.page ? <span style={{ wordBreak: 'break-all' }}> · {t.context.page}</span> : ''}
                      </div>

                      {/* Club-request form fields (B-5) — labelled rows */}
                      {t.category === 'club_request' && (
                        <div style={{
                          marginBottom: 10, padding: '10px 12px', borderRadius: 8,
                          background: 'rgba(90,74,124,0.08)', border: '1px solid rgba(90,74,124,0.18)',
                        }}>
                          {CLUB_REQUEST_FIELDS.filter(([k]) => t.context?.[k]).map(([k, label]) => (
                            <div key={k} style={{ display: 'flex', gap: 8, fontSize: 13.5, padding: '2px 0' }}>
                              <span style={{ width: 84, flexShrink: 0, fontWeight: 700, color: '#5A4A7C' }}>{label}</span>
                              <span style={{ color: '#2C2B28' }}>{t.context[k]}</span>
                            </div>
                          ))}
                          <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                            Vet → create the club in Clubs → invite them as founding admin → resolve.
                          </div>
                        </div>
                      )}

                      {/* Conversation thread */}
                      {(t.messages || []).map(m => (
                        <div
                          key={m.id}
                          style={{
                            marginBottom: 8, padding: '9px 12px', borderRadius: 8, fontSize: 13.5,
                            background: m.isStaff ? 'rgba(30,74,53,0.07)' : 'rgba(196,148,58,0.10)',
                            color: '#2C2B28',
                          }}
                        >
                          <strong style={{ color: m.isStaff ? '#1E4A35' : '#8A6220' }}>{m.senderName}:</strong>{' '}
                          {m.body}
                          <span style={{ display: 'block', fontSize: 11.5, color: '#999', marginTop: 2 }}>
                            {new Date(m.createdAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      ))}

                      <textarea
                        rows={2}
                        value={draft}
                        placeholder={`Reply to ticket #${t.ticket_no} — pings the member's phone and shows on their report page`}
                        onChange={e => setNoteDrafts(d => ({ ...d, [t.id]: e.target.value }))}
                        onClick={e => e.stopPropagation()}
                        style={{
                          width: '100%', boxSizing: 'border-box', fontSize: 13.5, padding: '8px 10px',
                          borderRadius: 8, border: '1px solid rgba(0,0,0,0.15)', resize: 'vertical',
                          fontFamily: 'inherit',
                        }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                        <button
                          className="period-btn active"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 16px' }}
                          disabled={busy || !draft.trim()}
                          onClick={(e) => { e.stopPropagation(); sendReply.mutate({ id: t.id, body: draft }); }}
                        >
                          <Send size={13} />
                          {sendReply.isPending ? 'Sending…' : 'Send reply'}
                        </button>
                        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                          {STATUSES.filter(s => s !== t.status).map(s => (
                            <button
                              key={s}
                              className="period-btn"
                              style={{ borderColor: STATUS_COLORS[s], color: STATUS_COLORS[s] }}
                              disabled={busy}
                              onClick={(e) => { e.stopPropagation(); changeStatus(t, s); }}
                            >
                              → {STATUS_LABELS[s]}
                            </button>
                          ))}
                        </span>
                        {savedFlash === t.id && !busy && (
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#1E4A35' }}>Sent ✓</span>
                        )}
                        {(sendReply.isError || update.isError) && (
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#A33' }}>
                            Failed: {(sendReply.error || update.error)?.response?.data?.error || (sendReply.error || update.error)?.message || 'unknown error'}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PlatformLayout>
  );
}
